import { gql } from '@apollo/client';

export const GET_SUBSCRIBER_PAGE = gql`
  query ELECTION_SERVICE_getSubscriberPage(
    $groupId: String!
    $periodId: String
    $sortBy: SortBy = NAME
    $sortOrder: SortOrder = ASC
    $filter: Filter = ALL
    $name: String
    $electionStatuses: [ElectionStatus!] = []
    $employeeClasses: [String!] = []
    $page: Int = 1
  ) {
    getSubscriberPage(
      groupId: $groupId
      periodId: $periodId
      sortBy: $sortBy
      sortOrder: $sortOrder
      filter: $filter
      name: $name
      electionStatuses: $electionStatuses
      employeeClasses: $employeeClasses
      page: $page
    ) {
      result {
        id
        ssn
        selfEnrollStatus
        subSERole
        seInviteDate
        productRates
        userId
        periodId
        groupId
        phone
        email
        employeeClass
        common
        custom
        choice {
          productLineId
          productId
          productName
          dependentIds
        }
        interestedProductLines {
          productLineId
          value
          dependentIds
        }
        productLineToCarrier
        electionStatus
        history {
          reason
          date
          actions
        }
        interestedProductLines {
          productLineId
          dependentIds
          value
        }
      }
      totalElements
      currentPage
      totalPages
    }
  }
`;

export const GET_POSSIBLE_CHOICE = gql`
  query ELECTION_SERVICE_getPossibleChoice($periodId: String) {
    getPossibleChoice(periodId: $periodId) {
      productLineId
      portfolioId
      plans {
        id
        name
        eligibleClasses
        carrierId
      }
    }
  }
`;

export const UPDATE_SUBSCRIBERS = gql`
  mutation ELECTION_SERVICE_updateSubscribers($periodId: String!, $subscribers: [SubscriberInput]) {
    updateSubscribers(periodId: $periodId, subscribers: $subscribers)
  }
`;

export const REMOVE_SUBSCRIBER = gql`
  mutation ELECTION_SERVICE_removeSubscriber($id: String) {
    removeSubscriber(id: $id)
  }
`;

export const UPDATE_SUBSCRIBER_INFO = gql`
  mutation ELECTION_SERVICE_updateSubscriber($periodId: String!, $subscriber: SubscriberInput) {
    updateSubscriber(periodId: $periodId, subscriber: $subscriber)
  }
`;

export const COMPLETE_CHECKLIST_STEP = gql`
  mutation ELECTION_SERVICE_completeChecklistStep($subId: String!, $entityId: String!, $card: Card!) {
    completeChecklistStep(subId: $subId, entityId: $entityId, card: $card)
  }
`;

export const GET_SUBSCRIBER_BY_ID = gql`
  query ELECTION_SERVICE_getSubscriberById($id: String) {
    getSubscriberById(id: $id) {
      id
      ssn
      groupId
      selfEnrollStatus
      subSERole
      seInviteDate
      productRates
      userId
      periodId
      phone
      email
      employeeClass
      common
      custom
      choice {
        productLineId
        productId
        productName
        dependentIds
      }
      interestedProductLines {
        productLineId
        value
        dependentIds
      }
      electionStatus
      tasks {
        name
        description
        entityId
        checklist {
          checklistOptions {
            card
            buttonState
            completionStatus
          }
        }
        active
      }
      productLineToCarrier
      history {
        reason
        date
        actions
      }
      interestedProductLines {
        productLineId
        dependentIds
        value
      }
    }
  }
`;

export const GET_SUBSCRIBER_BY_ID_WITH_CHOICES = gql`
  query ELECTION_SERVICE_getSubscriberByIdWithChoices($id: String!, $periodId: String) {
    getSubscriberWithChoices(id: $id, periodId: $periodId) {
      id
      ssn
      groupId
      selfEnrollStatus
      subSERole
      seInviteDate
      productRates
      userId
      periodId
      phone
      email
      employeeClass
      common
      custom
      choice {
        productLineId
        productId
        productName
        dependentIds
      }
      interestedProductLines {
        productLineId
        value
        dependentIds
      }
      electionStatus
      tasks {
        name
        description
        entityId
        checklist {
          checklistOptions {
            card
            buttonState
            completionStatus
          }
        }
        active
      }
      productLineToCarrier
      history {
        reason
        date
        actions
      }
      interestedProductLines {
        productLineId
        dependentIds
        value
      }
    }
  }
`;

export const GET_SUBSCRIBER_BY_USER_ID_AND_GROUP_ID = gql`
  query ELECTION_SERVICE_getSubscriberByUserIdAndGroupId($groupId: String!) {
    getSubscriberByUserIdAndGroupId(groupId: $groupId) {
      id
      ssn
      groupId
      selfEnrollStatus
      subSERole
      seInviteDate
      productRates
      userId
      periodId
      phone
      email
      employeeClass
      common
      custom
      electionStatus
      tasks {
        name
        description
        entityId
        checklist {
          checklistOptions {
            card
            buttonState
            completionStatus
          }
        }
        active
      }
    }
  }
`;

export const REMOVE_DEPENDENT = gql`
  mutation ELECTION_SERVICE_removeDependent($subscriberId: String, $dependentId: String) {
    removeDependent(subscriberId: $subscriberId, dependentId: $dependentId)
  }
`;

export const ADD_OR_UPDATE_DEPENDENT = gql`
  mutation ELECTION_SERVICE_addOrUpdateDependent($subscriberId: String, $dependent: Map) {
    addOrUpdateDependent(subscriberId: $subscriberId, dependent: $dependent)
  }
`;

export const GET_STATISTIC = gql`
  query ELECTION_SERVICE_getStatistic($groupId: String!, $periodId: String) {
    getStatistic(groupId: $groupId, periodId: $periodId) {
      total
      elected
      electionsMissing
      waived
      invited
      registered
      rateStats
      subRates
      estimatedCost
    }
  }
`;

export const GET_QUOTING_STATISTIC = gql`
  query ELECTION_SERVICE_getQuotingStatistic($groupId: String!, $cartId: String!) {
    getQuotingStatistic(groupId: $groupId, cartId: $cartId) {
      eesOnly
      eesWithDeps
      depsTotal
      productLineStat {
        productLineId
        eesOnly
        eesWithDeps
        depsTotal
      }
    }
  }
`;

export const SUBMIT_ELECTION = gql`
  mutation ELECTION_SERVICE_submitElection($groupId: String!, $periodId: String) {
    submitElection(groupId: $groupId, periodId: $periodId)
  }
`;

export const GET_INVITED_SUBSCRIBERS = gql`
  query ELECTION_SERVICE_getInvitedSubscribers($periodId: String!) {
    getInvitedSubscribers(periodId: $periodId) {
      id
      ssn
      groupId
      selfEnrollStatus
      subSERole
      seInviteDate
      productRates
      userId
      periodId
      phone
      email
      employeeClass
      common
      custom
      choice {
        productLineId
        productId
        productName
        dependentIds
      }
    }
  }
`;

export const GET_ENROLLS_BY_USER_ID = gql`
  query ELECTION_SERVICE_getEnrollsByUserId {
    getEnrollsByUserId
  }
`;

export const FINISH_ENROLL = gql`
  mutation ELECTION_SERVICE_finishEnroll($subId: String) {
    finishEnroll(subId: $subId)
  }
`;

export const REMIND_FINISH_SE = gql`
  query ELECTION_SERVICE_remindFinishSE($periodId: String!, $subIds: [String!]!) {
    remindFinishSE(periodId: $periodId, subIds: $subIds)
  }
`;

export const GET_EMPLOYEE_CLASSES = gql`
  query ELECTION_SERVICE_getEmployeeClasses($groupId: String!, $periodId: String) {
    getEmployeeClasses(groupId: $groupId, periodId: $periodId) {
      id
      name
      type
      periodId
      groupId
    }
  }
`;

export const GET_EMPLOYEE_CLASSES_WITH_EE = gql`
  query ELECTION_SERVICE_getEmployeeClassesWithEe($groupId: String!, $periodId: String) {
    getEmployeeClassesWithEe(groupId: $groupId, periodId: $periodId) {
      id
      name
      type
      periodId
      groupId
    }
  }
`;

export const UPDATE_EMPLOYEE_CLASSES = gql`
  mutation ELECTION_SERVICE_updateEligibleClasses(
    $groupId: String!
    $employeeClasses: [EligibleClass]
    $periodId: String
  ) {
    updateEligibleClasses(groupId: $groupId, periodId: $periodId, employeeClasses: $employeeClasses)
  }
`;

export const UPDATE_PRODUCT_CLASSES = gql`
  mutation ELECTION_SERVICE_updateProductClasses(
    $groupId: String!
    $productClassesMapping: [ProductClassesMapping]
    $periodId: String
  ) {
    updateProductClasses(groupId: $groupId, periodId: $periodId, productClassesMapping: $productClassesMapping)
  }
`;

export const GET_SUBS_VALIDATIONS = gql`
  query ELECTION_SERVICE_getSubsValidations($groupId: String!, $periodId: String) {
    getSubsValidations(groupId: $groupId, periodId: $periodId)
  }
`;

export const APPLY_ELECTION_CHANGE_REASON = gql`
  mutation ELECTION_SERVICE_applyElectionChangeReason(
    $domainId: String
    $subId: String!
    $reason: String!
    $date: String!
  ) {
    applyElectionChangeReason(domainId: $domainId, subId: $subId, reason: $reason, date: $date)
  }
`;

export const COMPLETE_ELECTION_CHANGE_STEP = gql`
  mutation ELECTION_SERVICE_completeElectionChangeStep($subId: String!, $card: Card!) {
    completeElectionChangeStep(subId: $subId, card: $card)
  }
`;

export const GET_SUBSCRIBER_PAGE_FOR_ENROLLMENT = gql`
  query ELECTION_SERVICE_getSubscriberPageForEnrollment(
    $groupId: String!
    $periodId: String!
    $sortBy: SortBy = NAME
    $sortOrder: SortOrder = DESC
    $filter: Filter = ALL
    $name: String = ""
    $status: [SelfEnrollStatus!] = []
    $progress: [Progress!]! = []
    $employeeClasses: [String!]! = []
    $page: Int = 1
  ) {
    getSubscriberPageForEnrollment(
      groupId: $groupId
      periodId: $periodId
      sortBy: $sortBy
      sortOrder: $sortOrder
      filter: $filter
      name: $name
      status: $status
      progress: $progress
      employeeClasses: $employeeClasses
      page: $page
    ) {
      result {
        id
        ssn
        selfEnrollStatus
        subSERole
        seInviteDate
        productRates
        userId
        periodId
        phone
        email
        employeeClass
        common
        custom
        choice {
          productLineId
          productId
          productName
          dependentIds
        }
        interestedProductLines {
          productLineId
          value
          dependentIds
        }
        productLineToCarrier
        electionStatus
        history {
          reason
          date
          actions
        }
        addOnDate
        addOnMethod
        progress
      }
      totalElements
      currentPage
      totalPages
    }
  }
`;

export const GET_SUBSCRIBERS_PAGE_FOR_GROUP = gql`
  query ELECTION_SERVICE_getSubscribersPageForGroup(
    $groupId: String!
    $cartId: String
    $sortBy: SortBy = NAME
    $sortOrder: SortOrder = DESC
    $filter: Filter = ALL
    $name: String = ""
    $status: [SelfEnrollStatus!] = []
    $progress: [Progress!]! = []
    $addOnMethod: [AddOnMethod!]! = []
    $page: Int = 1
  ) {
    getSubscribersPageForGroup(
      groupId: $groupId
      cartId: $cartId
      sortBy: $sortBy
      sortOrder: $sortOrder
      filter: $filter
      name: $name
      status: $status
      progress: $progress
      addOnMethod: $addOnMethod
      page: $page
    ) {
      result {
        id
        ssn
        groupId
        selfEnrollStatus
        subSERole
        seInviteDate
        productRates
        userId
        periodId
        phone
        email
        employeeClass
        common
        custom
        choice {
          productLineId
          productId
          productName
          dependentIds
        }
        interestedProductLines {
          productLineId
          value
          dependentIds
        }
        productLineToCarrier
        electionStatus
        history {
          reason
          date
          actions
        }
        addOnDate
        addOnMethod
        progress
        interestedProductLines {
          productLineId
          dependentIds
          value
        }
      }
      totalElements
      currentPage
      totalPages
    }
  }
`;

export const GET_REQUIRED_SUB_INFO = gql`
  query ELECTION_SERVICE_getRequiredSubInfo($domainId: String) {
    getRequiredSubInfo(domainId: $domainId) {
      editSubFormId
      manageDependentFormId
      requiredFields
      productLineRequiredInfo
      quotingSubFormId
      quotingRequiredFields
    }
  }
`;

export const UPDATE_EMAIL = gql`
  mutation ELECTION_SERVICE_updateEmail($subId: String!, $email: String!) {
    updateEmail(subId: $subId, email: $email)
  }
`;

export const CAN_COMPLETE_ADD_EMPLOYEES = gql`
  query ELECTION_SERVICE_canCompleteAddEmployees($groupId: String!, $periodId: String!) {
    canCompleteAddEmployees(groupId: $groupId, periodId: $periodId)
  }
`;

export const CAN_COMPLETE_QUOTING_ADD_EMPLOYEES = gql`
  query ELECTION_SERVICE_canCompleteQuotingAddEmployees($groupId: String!) {
    canCompleteQuotingAddEmployees(groupId: $groupId)
  }
`;

export const CREATE_EMPLOYEES_FOR_GROUP = gql`
  mutation ELECTION_SERVICE_createEmployeesForGroup(
    $groupId: String!
    $addOnMethod: AddOnMethod!
    $employees: [Map!]!
  ) {
    createEmployeesForGroup(groupId: $groupId, addOnMethod: $addOnMethod, employees: $employees) {
      id
    }
  }
`;

export const ADD_FULL_EMPLOYEE_TO_GROUP = gql`
  mutation ELECTION_SERVICE_addFullEmployeeToGroup($groupId: String!, $employee: Map!) {
    addFullEmployeeToGroup(groupId: $groupId, employee: $employee) {
      id
    }
  }
`;

export const REMIND_COMPLETE_PROFILE = gql`
  query ELECTION_SERVICE_remindCompleteProfile($subId: String!) {
    remindCompleteProfile(subId: $subId)
  }
`;
